import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Star, Warning } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public reference-check survey (V1.3, Feb 2026).
 *
 * A referee lands here from an emailed link. No auth. Answers 5
 * fixed-schema questions, submits, done. Recruiter gets a summary email.
 * See /app/backend/routes/reference_checks.py for the matching endpoints.
 */
export default function PublicReferencePage() {
    const { token } = useParams();
    const [state, setState] = useState({ loading: true, data: null, error: null });
    const [submitted, setSubmitted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        overall_rating: 0,
        recommend_for_role: "",
        recommend_comment: "",
        strongest_skills: "",
        improvement_areas: "",
        would_rehire: "",
    });

    useEffect(() => {
        axios.get(`${API}/api/public/reference/${token}`)
            .then((r) => {
                setState({ loading: false, data: r.data, error: null });
                if (r.data?.already_submitted) setSubmitted(true);
            })
            .catch((ex) => setState({
                loading: false, data: null,
                error: ex?.response?.data?.detail || "Could not load survey.",
            }));
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (form.overall_rating < 1 || form.overall_rating > 5) {
            alert("Please pick a rating from 1 to 5 stars.");
            return;
        }
        if (!form.recommend_for_role) {
            alert("Please indicate whether you'd recommend the candidate for this role.");
            return;
        }
        if (!form.would_rehire) {
            alert("Please answer the last question about rehiring.");
            return;
        }
        if (form.strongest_skills.trim().length < 3) {
            alert("Please share the candidate's strongest skills.");
            return;
        }
        if (form.improvement_areas.trim().length < 3) {
            alert("Please share areas for improvement.");
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/api/public/reference/${token}/submit`, form);
            setSubmitted(true);
        } catch (ex) {
            alert(ex?.response?.data?.detail || "Could not submit response.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-reference-page">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
                {/* Brand header */}
                <div className="mb-8 pb-6 border-b border-[#E5E5E5]">
                    <p className="font-heading text-2xl font-black tracking-tighter">
                        naukri<span className="text-cyan-400">daddy</span>
                    </p>
                </div>

                {state.loading && (
                    <p className="text-sm text-[#525252]">Loading survey…</p>
                )}
                {state.error && (
                    <div className="border border-rose-200 bg-rose-50 p-6 flex items-start gap-3">
                        <Warning size={20} weight="bold" className="text-rose-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-bold text-rose-900">Survey unavailable</p>
                            <p className="text-sm text-rose-800 mt-1">{state.error}</p>
                        </div>
                    </div>
                )}
                {submitted && (
                    <div data-testid="reference-submitted" className="border border-emerald-200 bg-emerald-50 p-8 text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-emerald-100 border border-emerald-300 mb-4">
                            <CheckCircle size={28} weight="bold" className="text-emerald-700" />
                        </div>
                        <h1 className="font-heading text-3xl font-black text-emerald-900 mb-2">Thank you ✓</h1>
                        <p className="text-emerald-800">
                            Your response has been shared with the hiring team. You&apos;re free to close this tab.
                        </p>
                    </div>
                )}

                {!submitted && !state.loading && state.data && (
                    <form onSubmit={submit} className="space-y-6">
                        <div className="border-b border-[#E5E5E5] pb-6">
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">Reference check</p>
                            <h1 className="font-heading text-3xl font-black tracking-tight text-ink">
                                Tell us about <span className="text-cobalt">{state.data.candidate_name || "the candidate"}</span>
                            </h1>
                            <p className="text-sm text-[#525252] mt-2">
                                Hi <b>{state.data.referee_name}</b> — you were listed as their {state.data.referee_relationship}.
                                It takes about 2 minutes. Your response is confidential.
                            </p>
                            {state.data.job_title && (
                                <p className="text-[11px] text-[#A3A3A3] mt-3">
                                    Role: {state.data.job_title}
                                    {state.data.client_name ? ` @ ${state.data.client_name}` : ""}
                                </p>
                            )}
                        </div>

                        {/* Q1 — overall rating */}
                        <div>
                            <label className="block text-sm font-bold text-ink mb-1">
                                1. Overall, how would you rate {state.data.candidate_name || "the candidate"}?
                            </label>
                            <p className="text-xs text-[#525252] mb-3">1 = below expectations · 5 = exceptional</p>
                            <div className="flex items-center gap-1" data-testid="rating-stars">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button
                                        key={n}
                                        type="button"
                                        data-testid={`rating-star-${n}`}
                                        onClick={() => setForm({ ...form, overall_rating: n })}
                                        className={`p-2 transition-colors ${form.overall_rating >= n ? "text-amber-500" : "text-[#D4D4D4] hover:text-amber-400"}`}
                                    >
                                        <Star size={28} weight={form.overall_rating >= n ? "fill" : "regular"} />
                                    </button>
                                ))}
                                <span className="ml-3 text-sm font-bold text-[#525252]">
                                    {form.overall_rating > 0 ? `${form.overall_rating}/5` : "—"}
                                </span>
                            </div>
                        </div>

                        {/* Q2 — recommend for role */}
                        <div>
                            <label className="block text-sm font-bold text-ink mb-2">
                                2. Would you recommend {state.data.candidate_name?.split(" ")[0] || "them"} for this role
                                {state.data.job_title ? ` (${state.data.job_title})` : ""}?
                            </label>
                            <div className="flex gap-2" data-testid="recommend-buttons">
                                {["yes", "no"].map((v) => (
                                    <button
                                        key={v}
                                        type="button"
                                        data-testid={`recommend-${v}`}
                                        onClick={() => setForm({ ...form, recommend_for_role: v })}
                                        className={`px-4 py-2 border text-sm font-bold uppercase tracking-wider transition-colors ${form.recommend_for_role === v ? "border-ink bg-ink text-white" : "border-[#E5E5E5] text-[#525252] hover:border-ink"}`}
                                    >
                                        {v === "yes" ? "Yes" : "No"}
                                    </button>
                                ))}
                            </div>
                            <textarea
                                data-testid="recommend-comment"
                                value={form.recommend_comment}
                                onChange={(e) => setForm({ ...form, recommend_comment: e.target.value })}
                                placeholder="Anything to add? (optional)"
                                rows={2}
                                className="w-full mt-3 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                            />
                        </div>

                        {/* Q3 — strongest skills */}
                        <div>
                            <label className="block text-sm font-bold text-ink mb-2">
                                3. What are their strongest skills?
                            </label>
                            <textarea
                                data-testid="strongest-skills"
                                required
                                value={form.strongest_skills}
                                onChange={(e) => setForm({ ...form, strongest_skills: e.target.value })}
                                placeholder="e.g. Clean architecture, ownership, cross-team collaboration…"
                                rows={3}
                                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                            />
                        </div>

                        {/* Q4 — improvement areas */}
                        <div>
                            <label className="block text-sm font-bold text-ink mb-2">
                                4. Areas for improvement?
                            </label>
                            <textarea
                                data-testid="improvement-areas"
                                required
                                value={form.improvement_areas}
                                onChange={(e) => setForm({ ...form, improvement_areas: e.target.value })}
                                placeholder="Where could they grow further?"
                                rows={3}
                                className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink"
                            />
                        </div>

                        {/* Q5 — would rehire */}
                        <div>
                            <label className="block text-sm font-bold text-ink mb-2">
                                5. Would you rehire them?
                            </label>
                            <div className="flex gap-2" data-testid="rehire-buttons">
                                {["yes", "maybe", "no"].map((v) => (
                                    <button
                                        key={v}
                                        type="button"
                                        data-testid={`rehire-${v}`}
                                        onClick={() => setForm({ ...form, would_rehire: v })}
                                        className={`px-4 py-2 border text-sm font-bold uppercase tracking-wider transition-colors ${form.would_rehire === v ? "border-ink bg-ink text-white" : "border-[#E5E5E5] text-[#525252] hover:border-ink"}`}
                                    >
                                        {v}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="pt-4 border-t border-[#E5E5E5]">
                            <button
                                type="submit"
                                data-testid="submit-reference-btn"
                                disabled={saving}
                                className="bg-ink hover:bg-cobalt disabled:opacity-60 text-white px-6 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2"
                            >
                                {saving ? "Submitting…" : "Submit reference"}
                            </button>
                            <p className="text-[11px] text-[#A3A3A3] mt-3">
                                By submitting, you confirm your response is honest and to the best of your knowledge.
                            </p>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
